import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { supabase } from "../lib/supabaseClient";
import logoImage from "@/assets/legacyloop-white-trans.png";
import { useAuth } from "@/hooks/useAuth";

export default function Login() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useAuth();
  const modeParam = new URLSearchParams(window.location.search).get("mode");
  const isSignup = modeParam === "signup";

  // honor ?redirect=/some/path; default to /dashboard
  const search = new URLSearchParams(window.location.search);
  const nextPath = search.get("redirect") || "/dashboard";
  const setupPath = `/welcome-setup?next=${encodeURIComponent(nextPath)}`;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      setLocation(isSignup ? setupPath : nextPath);
    }
  }, [isAuthenticated, isLoading, isSignup, nextPath, setLocation, setupPath]);

  async function onForgot() {
    setErr("");
    setNotice("");
    if (!email) {
      setErr("Enter your email above, then choose Forgot password.");
      return;
    }
    setLoading(true);
    try {
      // resetPasswordForEmail never reveals whether an account exists, so we always
      // show the same neutral confirmation. The link opens the /reset-password page.
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setNotice(`If an account exists for ${email}, we sent a link to reset your password.`);
    } catch (e: any) {
      setErr(e?.message || "Could not send the reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}${setupPath}` }
        });
        if (error) {
          // Supabase typically returns messages like "User already registered"
          if ((error.message || "").toLowerCase().includes("already")) {
            setErr("That email is already registered. Want to sign in instead?");
            // Optional: turn the current UI into Sign In by updating the URL:
            // setLocation(`/login?redirect=${encodeURIComponent(nextPath)}`);
          } else {
            setErr(error.message);
          }
        } else {
          if (!data.session) {
            setErr("Check your email to confirm your account, then continue.");
          } else {
            await queryClient.refetchQueries({ queryKey: ["/api/user"] });
            setLocation(setupPath);
          }
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          setErr(error.message);
        } else {
          await queryClient.refetchQueries({ queryKey: ["/api/user"] });
          setLocation(nextPath);
        }
      }
    } catch (e: any) {
      setErr(e?.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white font-sans">
      {/* Header */}
      <header className="py-6 px-6 border-b border-[#1B263B]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={logoImage} alt="LegacyLoop" className="h-12 w-auto" />
          <h1 className="text-lg font-bold text-[#E0A458]">
            {isSignup ? "Create Account" : "Sign In"}
          </h1>
        </div>
      </header>

      {/* Main */}
      <main className="py-16 px-6">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold text-[#E0A458]">
            {isSignup ? "Join LegacyLoop Today" : "Welcome Back"}
          </h2>
          <p className="text-slate-300 mt-2">
            {isSignup
              ? "Create an account to start preserving your athlete’s journey."
              : "Sign in to continue your athlete’s journey."}
          </p>
        </div>

        <div className="mt-8 max-w-md mx-auto">
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block mb-2 text-sm text-slate-300">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl bg-[#0f2740] border border-[#1B263B] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E0A458]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm text-slate-300">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl bg-[#0f2740] border border-[#1B263B] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E0A458]"
                placeholder="••••••••"
              />
            </div>
            {!isSignup && (
              <div className="text-right -mt-2">
                <button
                  type="button"
                  onClick={onForgot}
                  disabled={loading}
                  className="text-sm text-[#E0A458] hover:underline disabled:opacity-60"
                >
                  Forgot password?
                </button>
              </div>
            )}
            {notice && <p className="text-green-400 text-sm">{notice}</p>}
            {err && <p className="text-red-400 text-sm">{err}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-white text-black py-3 font-medium hover:opacity-90 disabled:opacity-60"
            >
              {loading ? (isSignup ? "Creating…" : "Signing in…") : (isSignup ? "Create Account" : "Sign In")}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
